import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import {
  FileText,
  Briefcase,
  GraduationCap,
  Heart,
  Globe,
  CheckCircle,
  AlertCircle,
  Plane,
  ArrowRight,
  Euro,
  Zap,
  Clock,
  Users
} from 'lucide-react';

const VisaInformationPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="visa-info-page">
      <Navbar />

      {/* Hero */}
      <section className="py-16 md:py-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-babyblue-200/40 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-babyblue-100 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <FileText className="w-4 h-4" />
              Integration Module
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-6">
              How to Get a Visa in Germany
            </h1>
            <p className="text-xl text-babyblue-600 leading-relaxed max-w-3xl mx-auto">
              Your complete guide to entering and staying in Germany legally.
            </p>
          </div>
        </div>
      </section>

      {/* Step 1: Reason */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-8 md:p-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg">1</div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800">Step 1: Reason</h2>
            </div>
            <p className="text-babyblue-700 text-lg leading-relaxed">
              You cannot come to Germany just to chill — you must provide a reason for your stay.
              The type of visa you receive depends entirely on <strong>why</strong> you want to be in Germany.
            </p>
          </div>
        </div>
      </section>

      {/* Different Types of Visas */}
      <section className="py-8 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-babyblue-800 mb-8 text-center">
            Different Types of Visas
          </h2>

          {/* Work Visa */}
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-8 mb-6" data-testid="visa-work-card">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-babyblue-100 rounded-xl">
                <Briefcase className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h3 className="font-heading text-2xl font-bold text-babyblue-800 mb-2">The Work Visa</h3>
                <p className="text-babyblue-700 leading-relaxed">
                  You apply for a job from your home country. Once you get approved and sign the working agreement,
                  you are qualified for a visa.
                </p>
              </div>
            </div>
          </div>

          {/* Fast Route - EU Blue Card */}
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl shadow-md border-2 border-amber-300 p-8 mb-6" data-testid="visa-fast-route-card">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-amber-200 rounded-xl">
                <Zap className="w-7 h-7 text-amber-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-2xl font-bold text-amber-900 mb-2">The Fast Route (EU Blue Card)</h3>
                <p className="text-amber-900 leading-relaxed mb-4">
                  If you have a university degree and a job offer that pays you above the salary threshold, you qualify
                  for an accelerated EU Blue Card.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Euro className="w-5 h-5 text-amber-700" />
                      <span className="font-semibold text-amber-900">Standard Threshold</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-900">€50,700</p>
                    <p className="text-sm text-amber-800">per year (regular jobs)</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Euro className="w-5 h-5 text-amber-700" />
                      <span className="font-semibold text-amber-900">Shortage Jobs</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-900">€45,934.20</p>
                    <p className="text-sm text-amber-800">per year (medical, IT, engineering)</p>
                  </div>
                </div>
                <p className="text-xs text-amber-800 mt-3 italic">
                  Note: These are 2025 thresholds and are updated annually by the German government.
                </p>
              </div>
            </div>
          </div>

          {/* EU Citizens */}
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-8 mb-6" data-testid="visa-eu-card">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Globe className="w-7 h-7 text-blue-700" />
              </div>
              <div>
                <h3 className="font-heading text-2xl font-bold text-babyblue-800 mb-2">EU Citizens</h3>
                <p className="text-babyblue-700 leading-relaxed">
                  Everyone with an <strong>EU passport</strong> has freedom of movement and the right to live and work
                  within the European Union — no visa required.
                </p>
              </div>
            </div>
          </div>

          {/* Chancenkarte */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-md border-2 border-green-300 p-8 mb-6" data-testid="visa-chancenkarte-card">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-green-200 rounded-xl">
                <Plane className="w-7 h-7 text-green-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-2xl font-bold text-green-900 mb-2">Die Chancenkarte (Opportunity Card)</h3>
                <p className="text-green-900 leading-relaxed mb-4">
                  Germany lets you enter the country and gives you up to <strong>one year</strong> to find a job on the ground.
                </p>
                <h4 className="font-semibold text-green-900 mb-2">Requirements:</h4>
                <ul className="space-y-2 text-green-900">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>A recognized degree <strong>OR</strong> score enough points based on age, work experience, and language skills.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Language proficiency: <strong>German A1</strong> or <strong>English B2</strong>.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Proof of sufficient financial savings to support yourself during the job search.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Student Visa */}
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-8 mb-6" data-testid="visa-student-card">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <GraduationCap className="w-7 h-7 text-blue-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-2xl font-bold text-babyblue-800 mb-2">Student Visa</h3>
                <p className="text-babyblue-700 leading-relaxed mb-4">
                  A popular route — university in Germany is very affordable (often free or low-cost) depending on
                  your citizenship and the federal state.
                </p>
                <h4 className="font-semibold text-babyblue-800 mb-2">Steps:</h4>
                <ul className="space-y-2 text-babyblue-700 mb-4">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Apply to a German University or <em>Fachhochschule</em> (University of Applied Sciences).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Receive an official <em>Immatrikulation</em> (enrollment) acceptance letter.</span>
                  </li>
                </ul>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Euro className="w-5 h-5 text-blue-700" />
                    <span className="font-semibold text-blue-900">Financial Requirement (Sperrkonto)</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">€11,904</p>
                  <p className="text-sm text-blue-800">minimum in a German blocked bank account as proof of funds (2025 rate)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Cheat Code - Family Reunification */}
          <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl shadow-md border-2 border-pink-300 p-8 mb-6" data-testid="visa-family-card">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-pink-200 rounded-xl">
                <Heart className="w-7 h-7 text-pink-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-2xl font-bold text-pink-900 mb-2">
                  Cheat Code: Family Reunification
                </h3>
                <p className="text-pink-900 leading-relaxed">
                  If you are <strong>marrying</strong> or have a <strong>parent working in Germany</strong>, you can join
                  them through the family reunification visa (<em>Familienzusammenführung</em>). This usually requires a
                  basic German level of <strong>A1</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Required Documents */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-babyblue-50 rounded-2xl border-2 border-babyblue-300 p-8 md:p-10" data-testid="visa-documents-card">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="w-8 h-8 text-primary" />
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800">
                Required Documents (For Every Visa Type)
              </h2>
            </div>
            <p className="text-babyblue-700 mb-6">
              No matter which visa you pick, the following documents are essential:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: <FileText className="w-5 h-5" />, label: 'Valid passport' },
                { icon: <CheckCircle className="w-5 h-5" />, label: 'Proof of reason (contract, acceptance letter, marriage cert.)' },
                { icon: <Euro className="w-5 h-5" />, label: 'Financial proof (bank statement, Sperrkonto, sponsorship)' },
                { icon: <Heart className="w-5 h-5" />, label: 'Health insurance (travel / incoming insurance before arrival)' },
              ].map((doc, i) => (
                <div key={i} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-babyblue-200">
                  <div className="text-primary">{doc.icon}</div>
                  <span className="text-babyblue-800 font-medium">{doc.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-babyblue-800 mb-8 text-center">
            How the Application Process Looks
          </h2>

          <div className="space-y-6">
            {/* Step 1 - Book */}
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 md:p-8 flex items-start gap-5" data-testid="visa-process-step-1">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xl">
                1
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-babyblue-800 mb-2">Book an Appointment</h3>
                <p className="text-babyblue-700 leading-relaxed">
                  Schedule a visa appointment at the closest German embassy or consulate to you. Demand is very high,
                  so <strong>book as early as possible</strong> — waiting times can be months long.
                </p>
              </div>
            </div>

            {/* Step 2 - Interview */}
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 md:p-8 flex items-start gap-5" data-testid="visa-process-step-2">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xl">
                2
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-babyblue-800 mb-2">Interview</h3>
                <p className="text-babyblue-700 leading-relaxed">
                  You present all required documents, give your fingerprints (biometrics), and answer a few questions
                  about yourself and why you want to go to Germany.
                </p>
              </div>
            </div>

            {/* Step 3 - Wait */}
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 md:p-8 flex items-start gap-5" data-testid="visa-process-step-3">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xl">
                3
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-babyblue-800 mb-2">Wait</h3>
                <p className="text-babyblue-700 leading-relaxed mb-3">
                  You leave your passport with the embassy. The process typically takes <strong>2 to 8 weeks</strong>.
                  If you are eligible, your visa will be printed inside your passport and mailed back to your address.
                </p>
                <div className="inline-flex items-center gap-2 bg-babyblue-100 text-primary px-3 py-1.5 rounded-full text-sm font-medium">
                  <Clock className="w-4 h-4" />
                  Typical wait: 2 – 8 weeks
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Modules */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-6">
            Continue Your Integration Journey
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Link
              to="/integration/healthcare-system"
              className="bg-white rounded-2xl p-6 border border-babyblue-200 hover:border-primary hover:shadow-lg transition-all"
              data-testid="link-healthcare-system"
            >
              <Heart className="w-7 h-7 text-red-500 mx-auto mb-2" />
              <p className="font-semibold text-babyblue-800">Healthcare System</p>
            </Link>
            <Link
              to="/careers/healthcare"
              className="bg-white rounded-2xl p-6 border border-babyblue-200 hover:border-primary hover:shadow-lg transition-all"
              data-testid="link-careers-healthcare"
            >
              <Briefcase className="w-7 h-7 text-primary mx-auto mb-2" />
              <p className="font-semibold text-babyblue-800">Healthcare Careers</p>
            </Link>
            <Link
              to="/careers/hospitality"
              className="bg-white rounded-2xl p-6 border border-babyblue-200 hover:border-primary hover:shadow-lg transition-all"
              data-testid="link-careers-hospitality"
            >
              <Users className="w-7 h-7 text-amber-500 mx-auto mb-2" />
              <p className="font-semibold text-babyblue-800">Hospitality Careers</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VisaInformationPage;
